/**
 * GodModeScene — Standalone scene for editing GOD mode parameters.
 * Accessible from TitleScene via "GOD" button.
 * Tabbed UI — one tab per category, content switches on tab click.
 */

import Phaser from 'phaser';
import { CANVAS_WIDTH, CANVAS_HEIGHT } from '@/config/GameConfig';
import { GOD, GOD_PARAMS, GodParam, resetGodMode, saveGodMode, saveAllTuning } from '@/config/GodMode';
import { createTechButton, fillTechRect, drawTechRect, TECH, showConfirmDialog } from '@/ui/TechUI';
import { SoundManager } from '@/systems/SoundManager';
import { t } from '@/i18n/I18n';

// ── Layout ──
const TOP_H = 52;
const TAB_BAR_H = 48;
const BOTTOM_H = 50;
const PANEL_W = 360;
const ROW_H = 24;
const CAT_HEADER_H = 28;
const TRACK_W = 130;

const CATEGORIES = [
  'robot', 'enemy', 'economy', 'workshop', 'wave', 'display', 'scale',
  'bars', 'popup', 'camera', 'cheat', 'billiard', 'jumper',
] as const;

const CAT_LABELS: Record<string, string> = {
  robot: '⚙ ROBOT', enemy: '👾 ENEMY', economy: '💰 ECONOMY', workshop: '🔧 WORKSHOP', wave: '🌊 WAVE',
  display: '🖥 DISPLAY', scale: '📐 SCALE', bars: '📊 BARS', popup: '💬 POPUP', camera: '🎥 CAMERA', cheat: '⚡ CHEATS',
  billiard: '🎱 BILLIARD', jumper: '🦘 JUMPER',
};
const CAT_SHORT: Record<string, string> = {
  robot: '⚙', enemy: '👾', economy: '💰', workshop: '🔧', wave: '🌊',
  display: '🖥', scale: '📐', bars: '📊', popup: '💬', camera: '🎥', cheat: '⚡',
  billiard: '🎱', jumper: '🦘',
};
const CAT_COLORS: Record<string, number> = {
  robot: 0x44aaff, enemy: 0xff6644, economy: 0xffcc00, workshop: 0x88ff44, wave: 0x44cc44,
  display: 0x66ddcc, scale: 0x88ccff, bars: 0x44ddaa, popup: 0xffaa66, camera: 0xaa88ff, cheat: 0xff44ff,
  billiard: 0x44ddff, jumper: 0x44dd88,
};

export class GodModeScene extends Phaser.Scene {
  private scrollContainer!: Phaser.GameObjects.Container;
  private scrollY = 0;
  private maxScroll = 0;
  private activeTab: string = 'robot';
  private wheelHandler: ((p: Phaser.Input.Pointer, gx: Phaser.GameObjects.GameObject[], dx: number, dy: number) => void) | null = null;
  private maskGfx: Phaser.GameObjects.Graphics | null = null;

  constructor() { super({ key: 'GodModeScene' }); }

  create(): void {
    this.scrollY = 0;
    this.wheelHandler = null;
    this.maskGfx = null;
    this.cameras.main.setBackgroundColor('#0a0a18');

    this.buildUI();

    this.input.keyboard?.on('keydown-ESC', () => {
      SoundManager.play('click');
      saveAllTuning();
      this.scene.start('TitleScene');
    });
  }

  private buildUI(): void {
    // Clear everything except camera
    this.children.removeAll();
    if (this.wheelHandler) {
      this.input.off('wheel', this.wheelHandler);
      this.wheelHandler = null;
    }
    if (this.maskGfx) { this.maskGfx.destroy(); this.maskGfx = null; }

    this.buildBackground();
    this.buildTabBar();
    this.buildContent();
    this.buildActionBar();
  }

  // ── Background ──

  private buildBackground(): void {
    const gfx = this.add.graphics();
    fillTechRect(gfx, 0, 0, CANVAS_WIDTH, TOP_H, 0x111122, 0.9);
    this.add.text(CANVAS_WIDTH / 2, TOP_H / 2, t('god.title'), {
      fontSize: '18px', color: '#ff88ff', fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(0.5);
    fillTechRect(gfx, 0, CANVAS_HEIGHT - BOTTOM_H, CANVAS_WIDTH, BOTTOM_H, 0x111122, 0.9);
  }

  // ── Tab bar ──

  private buildTabBar(): void {
    const tabBarY = TOP_H;
    const bgGfx = this.add.graphics();
    fillTechRect(bgGfx, 0, tabBarY, CANVAS_WIDTH, TAB_BAR_H, 0x0d0d1e, 0.9);

    const tabW = 44;
    const tabH = 18;
    const tabGap = 3;
    const perRow = 7;
    const totalRowW = perRow * tabW + (perRow - 1) * tabGap;
    const startX = (CANVAS_WIDTH - totalRowW) / 2 + tabW / 2;

    for (let i = 0; i < CATEGORIES.length; i++) {
      const cat = CATEGORIES[i];
      const col = i % perRow;
      const row = Math.floor(i / perRow);
      const tx = startX + col * (tabW + tabGap);
      const ty = tabBarY + 12 + row * (tabH + 3);
      const isActive = cat === this.activeTab;
      const color = CAT_COLORS[cat];

      const tabGfx = this.add.graphics();
      tabGfx.fillStyle(color, isActive ? 0.35 : 0.08);
      tabGfx.fillRoundedRect(tx - tabW / 2, ty - tabH / 2, tabW, tabH, 3);
      tabGfx.lineStyle(1, color, isActive ? 0.8 : 0.25);
      tabGfx.strokeRoundedRect(tx - tabW / 2, ty - tabH / 2, tabW, tabH, 3);
      if (isActive) {
        tabGfx.fillStyle(color, 0.9);
        tabGfx.fillRect(tx - tabW / 2 + 4, ty + tabH / 2 - 2, tabW - 8, 2);
      }

      const label = this.add.text(tx, ty, CAT_SHORT[cat], {
        fontSize: '12px', fontFamily: 'monospace',
      }).setOrigin(0.5).setAlpha(isActive ? 1 : 0.5);

      const hit = this.add.rectangle(tx, ty, tabW, tabH, 0x000000, 0)
        .setInteractive({ useHandCursor: true });

      // Tooltip on hover
      const tooltip = this.add.text(tx, ty - tabH / 2 - 8, (CAT_LABELS[cat] ?? cat).replace(/^. /, ''), {
        fontSize: '8px', color: '#' + color.toString(16).padStart(6, '0'),
        fontFamily: 'monospace', fontStyle: 'bold',
        backgroundColor: '#0a0a1aee', padding: { x: 4, y: 2 },
      }).setOrigin(0.5).setVisible(false).setDepth(10);

      hit.on('pointerover', () => {
        if (cat !== this.activeTab) {
          tabGfx.clear();
          tabGfx.fillStyle(color, 0.2);
          tabGfx.fillRoundedRect(tx - tabW / 2, ty - tabH / 2, tabW, tabH, 3);
          tabGfx.lineStyle(1, color, 0.5);
          tabGfx.strokeRoundedRect(tx - tabW / 2, ty - tabH / 2, tabW, tabH, 3);
          label.setAlpha(0.8);
        }
        tooltip.setVisible(true);
      });
      hit.on('pointerout', () => {
        if (cat !== this.activeTab) {
          tabGfx.clear();
          tabGfx.fillStyle(color, 0.08);
          tabGfx.fillRoundedRect(tx - tabW / 2, ty - tabH / 2, tabW, tabH, 3);
          tabGfx.lineStyle(1, color, 0.25);
          tabGfx.strokeRoundedRect(tx - tabW / 2, ty - tabH / 2, tabW, tabH, 3);
          label.setAlpha(0.5);
        }
        tooltip.setVisible(false);
      });
      hit.on('pointerdown', () => {
        if (cat === this.activeTab) return;
        SoundManager.play('click');
        this.activeTab = cat;
        this.buildUI();
      });
    }
  }

  // ── Content (active tab only) ──

  private buildContent(): void {
    const panelTop = TOP_H + TAB_BAR_H + 4;
    const panelH = CANVAS_HEIGHT - TOP_H - TAB_BAR_H - BOTTOM_H - 8;
    const panelX = (CANVAS_WIDTH - PANEL_W) / 2;

    // Panel background
    const bgGfx = this.add.graphics();
    fillTechRect(bgGfx, panelX - 10, panelTop, PANEL_W + 20, panelH, 0x0d0d1e, 0.85);
    drawTechRect(bgGfx, panelX - 10, panelTop, PANEL_W + 20, panelH, 0xff44ff, 0.3, 1);

    // Scrollable container
    this.scrollContainer = this.add.container(panelX, panelTop + 8);
    this.maskGfx = this.make.graphics({});
    this.maskGfx.fillRect(panelX - 10, panelTop, PANEL_W + 20, panelH);
    this.scrollContainer.setMask(this.maskGfx.createGeometryMask());

    let y = 4;
    const labelX = 10;
    const trackLeft = 140;
    const valueX = PANEL_W - 10;
    const cat = this.activeTab;
    const catColor = CAT_COLORS[cat] ?? 0xaaaaaa;
    const colorHex = '#' + catColor.toString(16).padStart(6, '0');

    // Category header
    const catBg = this.add.graphics();
    catBg.fillStyle(catColor, 0.1);
    catBg.fillRoundedRect(0, y - 6, PANEL_W, CAT_HEADER_H - 4, 4);
    catBg.lineStyle(1, catColor, 0.25);
    catBg.strokeRoundedRect(0, y - 6, PANEL_W, CAT_HEADER_H - 4, 4);
    this.scrollContainer.add(catBg);

    const accent = this.add.graphics();
    accent.fillStyle(catColor, 0.6);
    accent.fillRoundedRect(2, y - 2, 3, CAT_HEADER_H - 12, 1);
    this.scrollContainer.add(accent);

    this.scrollContainer.add(this.add.text(labelX, y + 4, CAT_LABELS[cat] ?? cat.toUpperCase(), {
      fontSize: '10px', color: colorHex, fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(0, 0.5));
    y += CAT_HEADER_H;

    // Params for active category
    const params = GOD_PARAMS.filter(p => p.category === cat);
    for (const param of params) {
      this.addParamRow(param, labelX, y, trackLeft, TRACK_W, valueX, catColor);
      y += ROW_H;
    }

    this.maxScroll = Math.max(0, y + 20 - panelH);
    this.scrollY = 0;

    // Wheel scrolling
    this.wheelHandler = (_p, _gx, _dx, dy) => {
      this.scrollY = Phaser.Math.Clamp(this.scrollY + dy * 0.5, 0, this.maxScroll);
      this.scrollContainer.y = panelTop + 8 - this.scrollY;
    };
    this.input.on('wheel', this.wheelHandler);
  }

  // ── Param row (slider or toggle) ──

  private addParamRow(
    param: GodParam, labelX: number, y: number,
    trackLeft: number, trackW: number, valueX: number,
    catColor: number,
    target?: Phaser.GameObjects.Container,
  ): void {
    const container = target ?? this.scrollContainer;
    // Label
    const labelText = this.add.text(labelX, y, param.label, {
      fontSize: '9px', color: '#bbbbdd', fontFamily: 'monospace',
    }).setOrigin(0, 0.5);
    container.add(labelText);

    // Help icon (?) with hover tooltip
    if (param.tip) {
      const helpX = trackLeft - 14;
      const helpIcon = this.add.text(helpX, y, '?', {
        fontSize: '8px', color: '#666688', fontFamily: 'monospace', fontStyle: 'bold',
        backgroundColor: '#1a1a2e', padding: { x: 2, y: 0 },
      }).setOrigin(0.5).setInteractive({ useHandCursor: true });
      container.add(helpIcon);

      const tipText = this.add.text(helpX, y - 14, param.tip, {
        fontSize: '7px', color: '#ddddff', fontFamily: 'monospace',
        backgroundColor: '#1a1a3aee', padding: { x: 6, y: 4 },
        wordWrap: { width: 200 },
      }).setOrigin(0, 1).setVisible(false).setDepth(20);
      container.add(tipText);

      helpIcon.on('pointerover', () => { helpIcon.setColor('#ffcc44'); tipText.setVisible(true); });
      helpIcon.on('pointerout', () => { helpIcon.setColor('#666688'); tipText.setVisible(false); });
    }

    if (param.type === 'slider') {
      const min = param.min ?? 0;
      const max = param.max ?? 5;
      const currentVal = GOD[param.key] as number;
      const norm = (currentVal - min) / (max - min);

      // Track
      const trackGfx = this.add.graphics();
      const drawTrack = (n: number) => {
        trackGfx.clear();
        trackGfx.fillStyle(0x222244, 0.6);
        trackGfx.fillRoundedRect(trackLeft, y - 2, trackW, 4, 2);
        trackGfx.fillStyle(catColor, 0.35);
        trackGfx.fillRoundedRect(trackLeft, y - 2, Math.max(2, n * trackW), 4, 2);
      };
      drawTrack(norm);
      container.add(trackGfx);

      // Value text
      const isInt = (max - min) > 10;
      const valText = this.add.text(valueX, y, isInt ? String(Math.round(currentVal)) : currentVal.toFixed(1), {
        fontSize: '9px', color: '#ffffff', fontFamily: 'monospace',
      }).setOrigin(1, 0.5);
      container.add(valText);

      // Knob
      const knobGfx = this.add.graphics();
      const drawKnob = (kx: number) => {
        knobGfx.clear();
        knobGfx.fillStyle(catColor, 0.7);
        knobGfx.fillCircle(kx, y, 5);
        knobGfx.lineStyle(1.5, 0xffffff, 0.4);
        knobGfx.strokeCircle(kx, y, 5);
      };
      drawKnob(trackLeft + norm * trackW);
      container.add(knobGfx);

      // Knob hit area
      const knob = this.add.rectangle(trackLeft + norm * trackW, y, 14, 18, 0x000000, 0)
        .setInteractive({ useHandCursor: true, draggable: true });
      container.add(knob);
      this.input.setDraggable(knob);

      const applySlider = (rawX: number) => {
        const clampedX = Phaser.Math.Clamp(rawX, trackLeft, trackLeft + trackW);
        const n = (clampedX - trackLeft) / trackW;
        const step = param.step ?? 0.1;
        const raw = min + n * (max - min);
        const snapped = Phaser.Math.Clamp(Math.round(raw / step) * step, min, max);
        (GOD as any)[param.key] = snapped;
        knob.x = clampedX;
        drawKnob(clampedX);
        drawTrack(n);
        valText.setText(isInt ? String(Math.round(snapped)) : snapped.toFixed(1));
        saveGodMode();
      };

      knob.on('drag', (_p: Phaser.Input.Pointer, dragX: number) => applySlider(dragX));

      // Track click
      const trackHit = this.add.rectangle(trackLeft + trackW / 2, y, trackW, 16, 0x000000, 0)
        .setInteractive({ useHandCursor: true });
      container.add(trackHit);
      trackHit.on('pointerdown', (p: Phaser.Input.Pointer) => {
        // Convert world X to local X relative to container
        let offsetX = 0;
        let node: Phaser.GameObjects.Container | null = container;
        while (node) {
          offsetX += node.x;
          node = (node.parentContainer ?? null) as Phaser.GameObjects.Container | null;
        }
        applySlider(p.worldX - offsetX);
      });

    } else if (param.type === 'toggle') {
      const currentVal = GOD[param.key] as boolean;
      const toggleW = 32;
      const toggleH = 14;
      const toggleX = trackLeft + 20;

      const toggleGfx = this.add.graphics();
      const drawToggle = (on: boolean) => {
        toggleGfx.clear();
        toggleGfx.fillStyle(on ? 0x22664a : 0x222244, 0.8);
        toggleGfx.fillRoundedRect(toggleX - toggleW / 2, y - toggleH / 2, toggleW, toggleH, toggleH / 2);
        toggleGfx.lineStyle(1, on ? 0x44ff88 : 0x444466, 0.6);
        toggleGfx.strokeRoundedRect(toggleX - toggleW / 2, y - toggleH / 2, toggleW, toggleH, toggleH / 2);
        const kx = on ? toggleX + toggleW / 2 - toggleH / 2 : toggleX - toggleW / 2 + toggleH / 2;
        toggleGfx.fillStyle(on ? 0x44ff88 : 0x666688, 1);
        toggleGfx.fillCircle(kx, y, 4);
      };
      drawToggle(currentVal);
      container.add(toggleGfx);

      const toggleLabel = this.add.text(toggleX + toggleW / 2 + 6, y, currentVal ? t('misc.on') : t('misc.off'), {
        fontSize: '8px', color: currentVal ? '#44ff88' : '#555566',
        fontFamily: 'monospace', fontStyle: 'bold',
      }).setOrigin(0, 0.5);
      container.add(toggleLabel);

      const toggleHit = this.add.rectangle(toggleX, y, toggleW + 12, toggleH + 6, 0x000000, 0)
        .setInteractive({ useHandCursor: true });
      container.add(toggleHit);

      toggleHit.on('pointerdown', () => {
        const newVal = !(GOD[param.key] as boolean);
        (GOD as any)[param.key] = newVal;
        drawToggle(newVal);
        toggleLabel.setText(newVal ? t('misc.on') : t('misc.off'));
        toggleLabel.setColor(newVal ? '#44ff88' : '#555566');
        saveGodMode();
        SoundManager.play('click');
      });
    }
  }

  // ── Action bar ──

  private buildActionBar(): void {
    const y = CANVAS_HEIGHT - BOTTOM_H / 2;
    createTechButton(this, CANVAS_WIDTH / 2 - 240, y, t('god.enemyScales'), {
      onClick: () => { SoundManager.play('clickConfirm'); this.scene.start('EnemyTuningScene', { returnTo: 'GodModeScene' }); },
      width: 110, height: 30, fontSize: '11px', color: 0xffaa44,
    });
    createTechButton(this, CANVAS_WIDTH / 2 - 100, y, t('god.resetAll'), {
      onClick: () => showConfirmDialog(this, t('god.resetConfirm'), () => {
        resetGodMode(); this.scene.restart();
      }),
      width: 90, height: 30, fontSize: '13px', color: TECH.DANGER,
    });
    createTechButton(this, CANVAS_WIDTH / 2 + 20, y, t('god.saveAll'), {
      onClick: () => showConfirmDialog(this, t('god.saveConfirm'), () => {
        saveAllTuning(); SoundManager.play('clickConfirm');
      }, { confirmColor: TECH.SUCCESS }),
      width: 80, height: 30, fontSize: '13px', color: TECH.SUCCESS,
    });
    createTechButton(this, CANVAS_WIDTH / 2 + 140, y, t('god.back'), {
      onClick: () => { SoundManager.play('click'); saveAllTuning(); this.scene.start('TitleScene'); },
      width: 80, height: 30, fontSize: '13px', color: TECH.COLOR,
    });
  }

  shutdown(): void {
    if (this.wheelHandler) {
      this.input.off('wheel', this.wheelHandler);
      this.wheelHandler = null;
    }
    if (this.maskGfx) { this.maskGfx.destroy(); this.maskGfx = null; }
  }
}
